import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { isFuture } from 'date-fns';
import type { ReactNode } from 'react';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';

import streakBackground from '#src/assets/streakBackground.webp';
import { LineupButtons } from '#src/components/lineup/LineupButtons/index.tsx';

import { ContenderLiveFooter } from './ContenderLiveFooter/index.tsx';
import { ContenderPveFooter } from './ContenderPveFooter/index.tsx';
import { ContenderUpcomingFooter } from './ContenderUpcomingFooter/index.tsx';
import type { ContenderFooter_so5LeaderboardContender } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 0 })`
  flex: 1;
  justify-content: center;
  border-radius: var(--unit);
  background: var(--c-nd-100);
  border: 1px solid transparent;

  &.threshold-reached {
    border: 1px solid transparent;
    background:
      url(${streakBackground}) center / cover padding-box,
      linear-gradient(
          133deg,
          rgba(255, 78, 34, 1) 0%,
          rgba(254, 243, 20, 1) 100%
        )
        border-box;
  }

  position: relative;
  &:before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--c-nl-400);
    border-radius: inherit;
    z-index: -1;
  }
`;

type Props = {
  so5LeaderboardContender: ContenderFooter_so5LeaderboardContender;
  onClickPrizePool?: () => void;
  onClickBonus?: () => void;
  showButtons?: boolean;
  showBonus?: boolean;
  forceLive?: boolean;
  customFooter?: ReactNode;
};

export const ContenderFooter = ({
  so5LeaderboardContender,
  onClickPrizePool,
  onClickBonus,
  showButtons,
  showBonus,
  forceLive,
  customFooter,
}: Props) => {
  const { so5Leaderboard, so5Lineup, managerTeam } = so5LeaderboardContender;

  if (
    (isFuture(so5Leaderboard.cutOffDate) || !so5Lineup?.confirmable) &&
    !forceLive
  ) {
    return (
      <>
        <Root>
          <ContenderUpcomingFooter
            so5LeaderboardContender={so5LeaderboardContender}
            onClickPrizePool={onClickPrizePool}
            onClickBonus={onClickBonus}
          />
        </Root>
        {!!showButtons && (
          <LineupButtons
            so5Leaderboard={so5Leaderboard}
            so5Lineup={so5Lineup}
            managerTeam={managerTeam}
          />
        )}
      </>
    );
  }

  const hasReachedThreshold =
    so5Lineup?.thresholdsStreakTask?.completedBy?.id === so5Lineup?.id;

  if (so5Leaderboard.pveOnly) {
    return (
      <ContenderPveFooter
        onClick={onClickPrizePool}
        so5LeaderboardContender={so5LeaderboardContender}
      />
    );
  }

  return (
    <Root
      className={classNames({
        'threshold-reached': hasReachedThreshold,
      })}
    >
      <ContenderLiveFooter
        so5LeaderboardContender={so5LeaderboardContender}
        onClickPrizePool={onClickPrizePool}
        showBonus={showBonus}
        customFooter={customFooter}
      />
    </Root>
  );
};

ContenderFooter.fragments = {
  so5LeaderboardContender: gql`
    fragment ContenderFooter_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Leaderboard {
        slug
        pveOnly
        cutOffDate
        ...LineupButtons_so5Leaderboard
      }
      managerTeam {
        id
        ...LineupButtons_managerTeam
      }
      so5Lineup {
        id
        user {
          slug
        }
        confirmable
        thresholdsStreakTask {
          id
          completedBy {
            id
          }
        }
        ...LineupButtons_so5Lineup
      }
      ...ContenderLiveFooter_so5LeaderboardContender
      ...ContenderUpcomingFooter_so5LeaderboardContender
      ...ContenderPveFooter_so5LeaderboardContender
    }
    ${ContenderLiveFooter.fragments.so5LeaderboardContender}
    ${ContenderUpcomingFooter.fragments.so5LeaderboardContender}
    ${LineupButtons.fragments.so5Leaderboard}
    ${LineupButtons.fragments.so5Lineup}
    ${LineupButtons.fragments.managerTeam}
    ${ContenderPveFooter.fragments.so5LeaderboardContender}
  ` as TypedDocumentNode<ContenderFooter_so5LeaderboardContender>,
};
